import React from 'react';
import { Box, Typography, Button, IconButton } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { useDropzone } from 'react-dropzone';
import { CloudUpload, Close } from '@material-ui/icons';
import prettyBytes from 'pretty-bytes';
import { AlertCard, AlertCardBody, getAlertCardColors } from '../../../../common/cards/alert-card/AlertCard';

const useStyles = makeStyles((theme: any) => ({
  container: {
    padding: theme.spacing(4),
    maxWidth: '800px',
    margin: '0 auto',
  },
  title: {
    fontSize: '24px',
    fontWeight: 700,
    marginBottom: theme.spacing(2),
  },
  subtitle: {
    fontSize: '14px',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(3),
  },
  uploadContainer: {
    width: '100%',
    border: `2px dashed ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(3),
    marginTop: theme.spacing(3),
    cursor: 'pointer',
    '&:hover': {
      borderColor: theme.palette.primary.main,
    },
  },
  uploadTitle: {
    fontSize: '18px',
    fontWeight: 600,
  },
  uploadSubtitle: {
    fontSize: '12px',
    color: theme.palette.text.secondary,
  },
  buttonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: theme.spacing(4),
    gap: theme.spacing(1),
  },
  button: {
    borderRadius: '6px',
    height: '38px',
    fontSize: '14px',
    textTransform: 'capitalize',
    alignSelf: 'start',
  },
  instructionsBox: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.shape.borderRadius,
  },
  instructionsTitle: {
    fontSize: '16px',
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  instructionsList: {
    fontSize: '14px',
    marginLeft: theme.spacing(2),
    '& li': {
      marginBottom: theme.spacing(0.5),
    },
  },
}));

interface Props {
  onNext: () => void;
  onUpload: (file: any) => void;
  uploadingFile: any;
}

export function UploadFileStep({ onNext, onUpload, uploadingFile }: Props) {
  const classes = useStyles();
  const { getRootProps, getInputProps } = useDropzone();
  const { darkTextColor, lightTextColor } = getAlertCardColors('warning');

  return (
    <Box className={classes.container}>
      <Typography className={classes.title}>Bulk Deactivate Volunteers</Typography>
      <Typography className={classes.subtitle}>
        Upload a CSV file containing email addresses of volunteers to deactivate. This action will retire the volunteers
        and send notifications to configured recipients.
      </Typography>

      <Box mt={3}>
        <AlertCard variant='warning' title='Warning'>
          <AlertCardBody>
            <Typography variant='subtitle2' style={{ fontSize: '14px', fontWeight: 400, color: lightTextColor }}>
              This will deactivate volunteers and remove them from all active enrollments and rosters. Notifications
              will be sent to configured recipients. This action{' '}
              <span style={{ fontWeight: 'bold', color: darkTextColor }}>cannot be easily undone</span>.
            </Typography>
          </AlertCardBody>
        </AlertCard>
      </Box>

      <Box className={classes.instructionsBox}>
        <Typography className={classes.instructionsTitle}>CSV Format Requirements:</Typography>
        <ul className={classes.instructionsList}>
          <li>The CSV must have a header row with a column named &ldquo;email&rdquo;</li>
          <li>Each row should contain one email address</li>
          <li>Example format:</li>
        </ul>
        <pre style={{ marginLeft: '2rem', fontSize: '12px' }}>
          email{'\n'}volunteer1@example.com{'\n'}volunteer2@example.com
        </pre>
      </Box>

      {!uploadingFile ? (
        <div {...getRootProps()} className={classes.uploadContainer}>
          <input {...getInputProps()} multiple={false} onChange={(event: any) => onUpload(event.target.files[0])} />
          <CloudUpload style={{ marginRight: '1rem', fontSize: '48px', color: '#9e9e9e' }} />
          <Box>
            <Typography className={classes.uploadTitle}>Upload CSV File</Typography>
            <Typography className={classes.uploadSubtitle}>Click or drag file to upload</Typography>
          </Box>
        </div>
      ) : (
        <div className={classes.uploadContainer}>
          <Box flex={1}>
            <Typography className={classes.uploadTitle}>{uploadingFile.name}</Typography>
            <Typography className={classes.uploadSubtitle}>{prettyBytes(uploadingFile.size)}</Typography>
          </Box>
          <IconButton onClick={() => onUpload(null)}>
            <Close />
          </IconButton>
        </div>
      )}

      <Box className={classes.buttonContainer}>
        <Button
          classes={{ root: classes.button }}
          variant='contained'
          color='primary'
          onClick={onNext}
          disabled={!uploadingFile}
        >
          Next
        </Button>
      </Box>
    </Box>
  );
}
